import { toast } from 'react-toastify';
import { getGlobalStore } from 'reduxState';
import { setUser } from 'reduxState/store/user/slice';
import { getRefreshedToken } from './tokenRefresh';
import { SERVER_ENDPOINT } from './constants';
import { MerchantSearchResult } from '../reduxState/store/performanceSummary/types';

export interface ErrorResponse {
  status: number;
  message: string;
}

const handleTooManyRequests = async (res: Response) => {
  if (res.status === 429) {
    toast.error("You've made too many requests in a short time. Please wait a moment and try again.");
  }
};

export const request = async (method: string, path: string, body?: Record<string, unknown>): Promise<Response> => {
  const store = getGlobalStore();
  const auth0AccessToken = store.getState().user.accessToken;

  const makeRequest = async (token: string): Promise<Response> => {
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-WF-UserAgent': 'partner-admin',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    return fetch(`${SERVER_ENDPOINT}${path}`, options);
  };

  let response = await makeRequest(auth0AccessToken);
  handleTooManyRequests(response);

  // If we get a 401, try to refresh the token and retry
  if (response.status === 401) {
    try {
      // Get a fresh token (shared mutex ensures only one refresh happens at a time)
      // This will automatically handle invalid_grant errors and force logout
      const newToken = await getRefreshedToken();

      // Update the token in Redux state
      const state = store.getState();
      store.dispatch(
        setUser({
          token: newToken,
          user: state.user.user,
          email: state.user.email,
          roles: state.user.roles,
        }),
      );

      // Retry the original request with the new token
      response = await makeRequest(newToken);
      handleTooManyRequests(response);
    } catch (error) {
      // Token refresh failed - forceLogout was already called in getRefreshedToken
      console.error('Token refresh failed in api.ts:', error);
      // Don't redirect here, forceLogout() in tokenRefresh.ts handles it
    }
  }

  return response;
};

export const searchMerchants = async (searchValue: string): Promise<MerchantSearchResult[]> => {
  try {
    const response = await request('GET', `/api/search?q=${encodeURIComponent(searchValue)}`);
    return await response.json();
  } catch (error) {
    console.error(error.message || error);
    return [];
  }
};

export const createTestTransactions = async (appId: number, trackingCode: string): Promise<any> => {
  const response = await request('POST', `/api/${appId}/application/test-transactions`, {
    applicationId: appId,
    trackingCode,
  });
  const responseJSON = await response.json();
  if (!response.ok) {
    throw new Error(responseJSON.ErrorMessage);
  }
  return responseJSON;
};
